/**
 * Customer Health Service
 * 
 * Makes M3.B3 + M3.E3 + M3.G3 true:
 * - B3: Customer success metrics observable
 * - E3: Customer health measurable
 * - G3: Churn risk detectable
 * 
 * Evidence criteria:
 * - Usage patterns are tracked
 * - Health scores are calculated
 * - Trends are visualizable
 * - Risk signals are explicit
 */

import { recordMetric } from './observability';

// ============================================================================
// Types
// ============================================================================

export type HealthStatus = 'healthy' | 'at_risk' | 'critical' | 'churned';

export interface CustomerHealth {
  customerId: string;
  customerName: string;
  
  // Health score (0-100)
  healthScore: number;
  healthStatus: HealthStatus;
  
  // Component scores
  components: HealthComponents;
  
  // Trends
  trend: 'improving' | 'stable' | 'declining';
  trendDelta: number; // Change from last period
  
  // Risk indicators
  riskSignals: RiskSignal[];
  
  // Engagement
  lastActiveAt: string;
  daysSinceActive: number;
  
  // Calculated at
  calculatedAt: string;
}

export interface HealthComponents {
  adoption: number;      // Feature usage breadth
  engagement: number;    // Active users / total seats
  frequency: number;     // Usage frequency
  depth: number;         // Advanced feature usage
  satisfaction: number;  // NPS/survey scores
  support: number;       // Support ticket health
}

export type RiskType = 
  | 'low_adoption'
  | 'declining_usage'
  | 'inactive_users'
  | 'no_priorities_published'
  | 'support_escalations'
  | 'contract_ending'
  | 'champion_left'
  | 'no_integrations';

export interface RiskSignal {
  type: RiskType;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  detectedAt: string;
  recommendation: string;
}

export interface UsageMetrics {
  customerId: string;
  period: { start: string; end: string };
  
  // User metrics
  totalSeats: number;
  activeUsers: number;
  newUsers: number;
  
  // Activity metrics
  logins: number;
  sessionsStarted: number;
  cyclesCompleted: number;
  prioritiesPublished: number;
  
  // Feature usage
  featuresUsed: string[];
  integrationsActive: number;
  
  // Support metrics
  ticketsOpened: number;
  ticketsResolved: number;
  avgResolutionTime: number; // hours
}

export interface HealthTrend {
  date: string;
  healthScore: number;
  components: HealthComponents;
}

// ============================================================================
// In-Memory Store
// ============================================================================

const healthStore: Map<string, CustomerHealth> = new Map();
const usageStore: Map<string, UsageMetrics[]> = new Map();
const trendStore: Map<string, HealthTrend[]> = new Map();

// ============================================================================
// Health Calculation
// ============================================================================

export async function calculateCustomerHealth(
  customerId: string,
  customerName: string,
  usage: UsageMetrics
): Promise<CustomerHealth> {
  // Calculate component scores
  const components = calculateComponents(usage);
  
  // Calculate overall health score (weighted average)
  const weights = {
    adoption: 0.20,
    engagement: 0.25,
    frequency: 0.15,
    depth: 0.15,
    satisfaction: 0.15,
    support: 0.10,
  };
  
  const healthScore = Math.round(
    components.adoption * weights.adoption +
    components.engagement * weights.engagement +
    components.frequency * weights.frequency +
    components.depth * weights.depth +
    components.satisfaction * weights.satisfaction +
    components.support * weights.support
  );
  
  // Determine status
  const healthStatus = getHealthStatus(healthScore);
  
  // Detect risk signals
  const riskSignals = detectRiskSignals(usage, components);
  
  // Calculate trend
  const previousHealth = healthStore.get(customerId);
  const trendDelta = previousHealth 
    ? healthScore - previousHealth.healthScore 
    : 0;
  const trend = trendDelta > 5 ? 'improving' : trendDelta < -5 ? 'declining' : 'stable';
  
  // Days since active
  const lastActive = new Date(usage.period.end);
  const daysSinceActive = Math.floor(
    (new Date().getTime() - lastActive.getTime()) / (24 * 60 * 60 * 1000)
  );
  
  const health: CustomerHealth = {
    customerId,
    customerName,
    healthScore,
    healthStatus,
    components,
    trend,
    trendDelta,
    riskSignals,
    lastActiveAt: usage.period.end,
    daysSinceActive,
    calculatedAt: new Date().toISOString(),
  };
  
  healthStore.set(customerId, health);
  
  // Store trend data
  const trends = trendStore.get(customerId) || [];
  trends.push({
    date: new Date().toISOString().split('T')[0],
    healthScore,
    components,
  });
  trendStore.set(customerId, trends.slice(-90)); // Keep 90 days
  
  recordMetric('usage', 'customer_health_calculated', healthScore, { 
    status: healthStatus,
    trend,
  });
  
  return health;
}

function calculateComponents(usage: UsageMetrics): HealthComponents {
  // Adoption: features used / total features
  const totalFeatures = 10; // Simplified
  const adoption = Math.min(100, (usage.featuresUsed.length / totalFeatures) * 100);
  
  // Engagement: active users / total seats
  const engagement = usage.totalSeats > 0 
    ? Math.min(100, (usage.activeUsers / usage.totalSeats) * 100)
    : 0;
  
  // Frequency: logins per user per week (target: 5)
  const periodDays = 7;
  const loginsPerUserPerWeek = usage.activeUsers > 0
    ? (usage.logins / usage.activeUsers) * (7 / periodDays)
    : 0;
  const frequency = Math.min(100, (loginsPerUserPerWeek / 5) * 100);
  
  // Depth: advanced feature usage
  const advancedFeatures = ['integrations', 'portfolios', 'analytics', 'api'];
  const advancedUsed = usage.featuresUsed.filter(f => advancedFeatures.includes(f)).length;
  const depth = Math.min(100, (advancedUsed / advancedFeatures.length) * 100);
  
  // Satisfaction: placeholder (would come from NPS/surveys)
  const satisfaction = 75; // Default
  
  // Support: inverse of ticket volume / resolution time
  const ticketHealth = usage.ticketsOpened === 0 
    ? 100
    : Math.max(0, 100 - (usage.ticketsOpened * 5) - (usage.avgResolutionTime > 24 ? 20 : 0));
  const support = Math.min(100, ticketHealth);
  
  return {
    adoption: Math.round(adoption),
    engagement: Math.round(engagement),
    frequency: Math.round(frequency),
    depth: Math.round(depth),
    satisfaction,
    support: Math.round(support),
  };
}

function getHealthStatus(score: number): HealthStatus {
  if (score >= 70) return 'healthy';
  if (score >= 50) return 'at_risk';
  if (score >= 20) return 'critical';
  return 'churned';
}

function detectRiskSignals(usage: UsageMetrics, components: HealthComponents): RiskSignal[] {
  const signals: RiskSignal[] = [];
  
  // Low adoption
  if (components.adoption < 30) {
    signals.push({
      type: 'low_adoption',
      severity: components.adoption < 15 ? 'high' : 'medium',
      description: `Only ${components.adoption}% of features are being used`,
      detectedAt: new Date().toISOString(),
      recommendation: 'Schedule feature training session',
    });
  }
  
  // Declining engagement
  if (components.engagement < 40) {
    signals.push({
      type: 'inactive_users',
      severity: components.engagement < 20 ? 'high' : 'medium',
      description: `Only ${components.engagement}% of seats are active`,
      detectedAt: new Date().toISOString(),
      recommendation: 'Reach out to inactive users',
    });
  }
  
  // No priorities published
  if (usage.prioritiesPublished === 0) {
    signals.push({
      type: 'no_priorities_published',
      severity: 'high',
      description: 'No priorities have been published this period',
      detectedAt: new Date().toISOString(),
      recommendation: 'Check if customer is stuck in workflow',
    });
  }
  
  // Support escalations
  if (usage.ticketsOpened > 5 || usage.avgResolutionTime > 48) {
    signals.push({
      type: 'support_escalations',
      severity: usage.ticketsOpened > 10 ? 'critical' : 'high',
      description: `${usage.ticketsOpened} support tickets with ${usage.avgResolutionTime}h avg resolution`,
      detectedAt: new Date().toISOString(),
      recommendation: 'Review support tickets and escalate if needed',
    });
  }
  
  // No integrations
  if (usage.integrationsActive === 0) {
    signals.push({
      type: 'no_integrations',
      severity: 'low',
      description: 'No integrations are configured',
      detectedAt: new Date().toISOString(),
      recommendation: 'Suggest relevant integrations',
    });
  }
  
  return signals;
}

// ============================================================================
// Health Queries
// ============================================================================

export async function getCustomerHealth(customerId: string): Promise<CustomerHealth | null> {
  return healthStore.get(customerId) || null;
}

export async function listCustomerHealth(
  filter?: { status?: HealthStatus; minScore?: number; maxScore?: number }
): Promise<CustomerHealth[]> {
  let health = Array.from(healthStore.values());
  
  if (filter?.status) {
    health = health.filter(h => h.healthStatus === filter.status);
  }
  if (filter?.minScore !== undefined) {
    health = health.filter(h => h.healthScore >= filter.minScore!);
  }
  if (filter?.maxScore !== undefined) {
    health = health.filter(h => h.healthScore <= filter.maxScore!);
  }
  
  return health.sort((a, b) => a.healthScore - b.healthScore);
}

export async function getHealthTrends(customerId: string): Promise<HealthTrend[]> {
  return trendStore.get(customerId) || [];
}

// ============================================================================
// At-Risk Detection
// ============================================================================

export async function getAtRiskCustomers(): Promise<CustomerHealth[]> {
  return listCustomerHealth({ status: 'at_risk' });
}

export async function getCriticalCustomers(): Promise<CustomerHealth[]> {
  return listCustomerHealth({ status: 'critical' });
}

export async function getChurnRisk(): Promise<{
  highRisk: CustomerHealth[];
  mediumRisk: CustomerHealth[];
  lowRisk: CustomerHealth[];
}> {
  const all = await listCustomerHealth();
  
  return {
    highRisk: all.filter(h => h.healthScore < 40 || h.riskSignals.some(r => r.severity === 'critical')),
    mediumRisk: all.filter(h => h.healthScore >= 40 && h.healthScore < 60),
    lowRisk: all.filter(h => h.healthScore >= 60 && h.healthScore < 80),
  };
}

// ============================================================================
// Health Dashboard Data
// ============================================================================

export interface HealthDashboard {
  generatedAt: string;
  
  // Summary
  totalCustomers: number;
  avgHealthScore: number;
  healthDistribution: Record<HealthStatus, number>;
  
  // Risk overview
  customersAtRisk: number;
  activeRiskSignals: number;
  topRisks: { type: RiskType; count: number }[];
  
  // Trends
  avgScoreTrend: 'improving' | 'stable' | 'declining';
  
  // Top performers
  topHealthy: CustomerHealth[];
  
  // Needs attention
  needsAttention: CustomerHealth[];
}

export async function getHealthDashboard(): Promise<HealthDashboard> {
  const all = await listCustomerHealth();
  
  // Health distribution
  const healthDistribution: Record<HealthStatus, number> = {
    healthy: 0,
    at_risk: 0,
    critical: 0,
    churned: 0,
  };
  
  for (const h of all) {
    healthDistribution[h.healthStatus]++;
  }
  
  // Average health
  const avgHealthScore = all.length > 0
    ? Math.round(all.reduce((sum, h) => sum + h.healthScore, 0) / all.length)
    : 100;
  
  // Risk analysis
  const allSignals = all.flatMap(h => h.riskSignals);
  const riskCounts = new Map<RiskType, number>();
  for (const signal of allSignals) {
    riskCounts.set(signal.type, (riskCounts.get(signal.type) || 0) + 1);
  }
  
  const topRisks = Array.from(riskCounts.entries())
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
  
  // Trend analysis
  const improving = all.filter(h => h.trend === 'improving').length;
  const declining = all.filter(h => h.trend === 'declining').length;
  const avgScoreTrend = improving > declining ? 'improving' : declining > improving ? 'declining' : 'stable';
  
  return {
    generatedAt: new Date().toISOString(),
    totalCustomers: all.length,
    avgHealthScore,
    healthDistribution,
    customersAtRisk: healthDistribution.at_risk + healthDistribution.critical,
    activeRiskSignals: allSignals.length,
    topRisks,
    avgScoreTrend,
    topHealthy: all.filter(h => h.healthStatus === 'healthy').slice(0, 5),
    needsAttention: all.filter(h => h.healthStatus !== 'healthy').slice(0, 10),
  };
}


